"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Textarea = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const if_defined_js_1 = require("lit/directives/if-defined.js");
const input_styles_1 = require("@/components/common/input/input.styles");
const textarea_auto_height_controller_1 = require("@/controllers/textarea-auto-height-controller");
const shared_styles_1 = require("@/stylesheets/shared.styles");
const utils_1 = require("@/utils");
let uniqueIdCounter = 0;
let Textarea = class Textarea extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.inputId = '';
        this.value = '';
        this.name = '';
        this.placeholder = '';
        this.ariaDescribedBy = null;
        this.rows = 2;
        this.disabled = false;
        this.ariaInvalid = null;
        this.defaultTextareaId = `mm-textarea-${uniqueIdCounter++}`;
        this.hasActions = false;
        this.autoHeight = new textarea_auto_height_controller_1.TextareaAutoHeightController(this, {
            getTextarea: () => this.textarea,
            getMinVisibleRows: () => this.visibleRows,
            getMaxVisibleRows: () => this.maxVisibleRows,
        });
    }
    render() {
        return this.renderTextarea();
    }
    // 호스트는 포커스를 받지 않으므로 실제 textarea로 넘긴다.
    focus(options) {
        this.textarea?.focus(options);
    }
    get textareaId() {
        return this.inputId || this.defaultTextareaId;
    }
    get textareaDescribedBy() {
        return this.ariaDescribedBy;
    }
    // 액션 슬롯이 채워지면 액션 줄이 높이를 보태므로 입력 영역은 1줄에서 시작한다.
    get visibleRows() {
        if (this.hasActions)
            return 1;
        return this.rows;
    }
    get maxVisibleRows() {
        return 5;
    }
    resizeToContent() {
        this.autoHeight.resizeToContent();
    }
    handleTextareaInput(event) {
        const target = event.target;
        event.stopPropagation();
        this.value = target.value;
        this.resizeToContent();
        this.dispatchInputEvent(target.value);
    }
    handleTextareaKeydown(_event) { }
    // 비어 있는 슬롯이 여백을 차지하지 않도록 내용이 있을 때만 드러낸다.
    // textarea-field처럼 슬롯을 전달받으면 전달용 slot의 slotchange도 올라오므로 currentTarget을 보고,
    // flatten으로 전달용 slot이 아닌 실제 내용을 센다.
    handleSlotChange(event) {
        const slot = event.currentTarget;
        slot.hidden = slot.assignedElements({ flatten: true }).length === 0;
        this.hasActions = Array.from(this.actionSlots).some(actionSlot => !actionSlot.hidden);
    }
    dispatchInputEvent(value) {
        (0, utils_1.emit)(this, 'input', { value });
    }
    renderTextarea() {
        return (0, lit_1.html) `
      <div class="textarea-control" aria-invalid=${(0, if_defined_js_1.ifDefined)(this.ariaInvalid ?? undefined)}>
        <slot name="leading" hidden @slotchange=${this.handleSlotChange}></slot>
        <textarea
          id=${this.textareaId}
          rows=${this.visibleRows}
          .value=${this.value}
          name=${(0, if_defined_js_1.ifDefined)(this.name || undefined)}
          placeholder=${(0, if_defined_js_1.ifDefined)(this.placeholder || undefined)}
          ?disabled=${this.disabled}
          aria-invalid=${(0, if_defined_js_1.ifDefined)(this.ariaInvalid ?? undefined)}
          aria-describedby=${this.textareaDescribedBy || lit_1.nothing}
          @input=${this.handleTextareaInput}
          @keydown=${this.handleTextareaKeydown}
        ></textarea>
        <slot name="trailing" hidden @slotchange=${this.handleSlotChange}></slot>
      </div>
    `;
    }
};
Textarea.styles = [
    shared_styles_1.resetStyles,
    input_styles_1.inputStyles,
    (0, lit_1.css) `
      .textarea-control {
        flex-direction: column;
        align-items: stretch;
        gap: 0;
      }

      slot[name='leading'],
      slot[name='trailing'] {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--space-2);
      }

      slot[name='leading'] {
        padding-block-start: var(--input-padding-block);
      }

      slot[name='trailing'] {
        padding-block-end: var(--input-padding-block);
      }

      slot[hidden] {
        display: none;
      }
    `,
];
__decorate([
    (0, decorators_js_1.property)({ type: String, attribute: 'input-id' })
], Textarea.prototype, "inputId", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Textarea.prototype, "value", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Textarea.prototype, "name", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Textarea.prototype, "placeholder", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String, attribute: 'aria-describedby' })
], Textarea.prototype, "ariaDescribedBy", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: Number })
], Textarea.prototype, "rows", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: Boolean, reflect: true })
], Textarea.prototype, "disabled", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String, attribute: 'aria-invalid' })
], Textarea.prototype, "ariaInvalid", void 0);
__decorate([
    (0, decorators_js_1.query)('textarea')
], Textarea.prototype, "textarea", void 0);
__decorate([
    (0, decorators_js_1.queryAll)('slot')
], Textarea.prototype, "actionSlots", void 0);
__decorate([
    (0, decorators_js_1.state)()
], Textarea.prototype, "defaultTextareaId", void 0);
__decorate([
    (0, decorators_js_1.state)()
], Textarea.prototype, "hasActions", void 0);
Textarea = __decorate([
    (0, decorators_js_1.customElement)('mm-textarea')
], Textarea);
exports.Textarea = Textarea;
//# sourceMappingURL=data:application/json;base64,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